import React, { useEffect, useState } from 'react';
import { View, ScrollView, StyleSheet, Alert } from 'react-native';
import { 
  Card, 
  Title, 
  Paragraph, 
  useTheme,
  Switch,
  List,
  Text,
  ActivityIndicator,
  Button,
  Dialog,
  Portal
} from 'react-native-paper';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { 
  loadLocationPreferences,
  saveLocationPreferences,
  setAutoDetectLocation,
  setShowZoomButtons,
  saveUIPreferences,
  loadUIPreferences
} from '../store/userSlice';
import { useAuth } from '../contexts/AuthContext';

export default function SettingsScreen() {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { locationPreferences, uiPreferences, isLoadingLocation } = useSelector((state: RootState) => state.user);
  const { deleteAccount } = useAuth();
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(loadLocationPreferences());
    dispatch(loadUIPreferences());
  }, [dispatch]);


  const handleAutoDetectToggle = async (value: boolean) => {
    dispatch(setAutoDetectLocation(value));
    const updatedPreferences = {
      ...locationPreferences,
      autoDetectLocation: value
    };
    dispatch(saveLocationPreferences(updatedPreferences));
  };

  const handleShowZoomButtonsToggle = async (value: boolean) => {
    dispatch(setShowZoomButtons(value));
    const updatedPreferences = {
      ...uiPreferences,
      showZoomButtons: value
    };
    dispatch(saveUIPreferences(updatedPreferences));
  };

  const showDeleteDialog = () => setDeleteDialogVisible(true);
  const hideDeleteDialog = () => setDeleteDialogVisible(false);

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      await deleteAccount();
      hideDeleteDialog();
    } catch (error) {
      console.error('Failed to delete account:', error);
      Alert.alert(
        'Error',
        'Failed to delete account. Please try again later.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsDeleting(false);
    }
  };


  if (isLoadingLocation) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" />
        <Text style={styles.loadingText}>Loading settings...</Text>
      </View>
    );
  }

  return (
    <>
      <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Card style={styles.card}>
          <Card.Content>
            <Title>Location Settings</Title>
            <Paragraph style={styles.description}>
              Configure how Ocean Insight Pro determines your location for weather and fishing data.
            </Paragraph>
          </Card.Content>
        </Card>

      <Card style={styles.card}>
        <List.Section>
          
          <List.Item
            title="Auto-detect Location"
            description="Automatically update location when the app starts"
            left={(props) => <List.Icon {...props} icon="map-marker-radius" />}
            right={() => (
              <Switch
                value={locationPreferences.autoDetectLocation}
                onValueChange={handleAutoDetectToggle}
              />
            )}
          />
        </List.Section>
      </Card>


      <Card style={styles.card}>
        <Card.Content>
          <Title>Map Display Settings</Title>
          <Paragraph style={styles.description}>
            Customize how the map interface appears.
          </Paragraph>
        </Card.Content>
        <List.Section>
          <List.Item
            title="Show Zoom Buttons"
            description="Display zoom in/out buttons on the map"
            left={(props) => <List.Icon {...props} icon="magnify-plus-outline" />}
            right={() => (
              <Switch
                value={uiPreferences.showZoomButtons}
                onValueChange={handleShowZoomButtonsToggle}
              />
            )}
          />
        </List.Section>
      </Card>

      <Card style={[styles.card, styles.dangerCard]}>
        <Card.Content>
          <Title style={styles.dangerTitle}>Account Management</Title>
          <Paragraph style={styles.description}>
            Permanently delete your account and all associated data.
          </Paragraph>
          <Button
            mode="contained"
            onPress={showDeleteDialog}
            style={styles.deleteButton}
            buttonColor={theme.colors.error}
            icon="delete-forever"
            disabled={isDeleting}
          >
            Delete Account
          </Button>
        </Card.Content>
      </Card>
      </ScrollView>

      <Portal>
        <Dialog visible={deleteDialogVisible} onDismiss={hideDeleteDialog}>
          <Dialog.Title>Delete Account</Dialog.Title>
          <Dialog.Content>
            <Paragraph>
              Are you sure you want to delete your account? This action cannot be undone.
            </Paragraph>
            <Paragraph style={styles.warningText}>
              All your data including saved waypoints, catch reports, and settings will be permanently deleted.
            </Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={hideDeleteDialog} disabled={isDeleting}>Cancel</Button>
            <Button 
              onPress={handleDeleteAccount} 
              loading={isDeleting}
              disabled={isDeleting}
              textColor={theme.colors.error}
            >
              Delete
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContent: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    margin: 16,
    marginBottom: 8,
    elevation: 2,
  },
  description: {
    marginTop: 8,
    opacity: 0.7,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    opacity: 0.7,
  },
  dangerCard: {
    marginBottom: 24,
  },
  dangerTitle: {
    color: '#d32f2f',
  },
  deleteButton: {
    marginTop: 16,
  },
  warningText: {
    marginTop: 12,
    fontWeight: '600',
    color: '#d32f2f',
  },
});